import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomFile } from '../../../../../../../models/interfaces/custom_file.interface';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../../../core/services/toast/toast.service';
import { UtilsService } from '../../../../../../../shared/utils/utils.service';
import { cloneDeep } from 'lodash-es';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { EnfafModalComponent } from "../../../../../../../components/enfaf-modal/enfaf-modal.component";
import { EnfafInputComponent } from "../../../../../../../components/enfaf-input/enfaf-input.component";
import { EnfafTextareaLightComponent } from "../../../../../../../components/enfaf-textarea-light/enfaf-textarea-light.component";
import { EnfafInputFileDraggerLightComponent } from "../../../../../../../components/enfaf-input-file-dragger-light/enfaf-input-file-dragger-light.component";

@Component({
    selector: 'app-modal-bulk-email',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        EnfafModalComponent,
        EnfafInputComponent,
        EnfafTextareaLightComponent,
        EnfafInputFileDraggerLightComponent
    ],
    templateUrl: './modal-bulk-email.component.html',
    styleUrl: './modal-bulk-email.component.scss'
})
export class ModalBulkEmailComponent implements OnInit, OnDestroy {
  courseId!: string;
  courseName!: string;
  showCloseAlert = false;
  emailForm: FormGroup = new FormGroup({});
  attachmentsToUpload: Array<CustomFile> = [];
  isSendingEmail = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalBulkEmailComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    this.courseId = receivedData?.courseId;
    this.courseName = receivedData?.courseName;
    if (!this.courseId || !this.courseName) {
      this.closeModal();
      return;
    }

    this.emailForm = new FormGroup({
      subject: new FormControl(null, Validators.required),
      message: new FormControl(null, Validators.required)
    });
  }

  listenForChanges(): void {
    this.emailForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    });
  }

  onDroppedFiles(files: Array<CustomFile>): void {
    if (!files?.length) return;

    for (const file of files) {
      file.name = this.utils.generateFileName(file.name);
    }

    this.attachmentsToUpload = files;
  }

  async sendEmail(): Promise<void> {
    try {
      this.isSendingEmail = true;
      const emailFormData = new FormData();
      const body = { ...this.emailForm.value, courseId: this.courseId };
      emailFormData.append('form', JSON.stringify(body));

      if (this.attachmentsToUpload.length) {
        for (let i = 0; i < this.attachmentsToUpload.length; i++) {
          emailFormData.append(`attachment_${i}`, this.attachmentsToUpload[i].file, this.attachmentsToUpload[i].name);
        }
      }

      const response = await firstValueFrom(this.api.sendBulkEmailToStudents(emailFormData));
      this.isSendingEmail = false;
      this.toast.presentSuccessToast(response.message);
      this.closeModal();
    } catch (errorResponse: any) {
      this.isSendingEmail = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error enviando email');
    }
  }
}
